import { ProFormSelect, ProFormSwitch } from '@ant-design/pro-components';
import { Flex, Form, InputNumber, Space, Switch, Table, Tag, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { extractMetricIdsFromExpression } from '../utils/metric-expression';

const EMPTY_ARRAY = [];

const buildMetricOptions = (metrics) =>
  metrics.map((metric) => ({
    label: metric.name,
    value: metric.id,
  }));

const getExistingItemState = (existingMetrics) =>
  new Map(
    existingMetrics
      .filter((metric) => metric.role === 'item')
      .map((metric) => [
        metric.metricId,
        {
          enabled: metric.enabled !== false,
          sortOrder: Number(metric.sortOrder || 0),
        },
      ]),
  );

const buildItemRows = (metricIds, metricsById, existingState) =>
  metricIds
    .map((metricId, index) => {
      const metric = metricsById.get(metricId);
      if (!metric) {
        return null;
      }
      const state = existingState.get(metricId);
      return {
        enabled: state?.enabled ?? true,
        metricId,
        metricName: metric.name,
        role: 'item',
        sortOrder: state?.sortOrder ?? (index + 1) * 10,
      };
    })
    .filter(Boolean)
    .sort((prev, next) => prev.sortOrder - next.sortOrder);

const toSaveMetricRows = (metrics) =>
  metrics.map(({ enabled, metricId, role, sortOrder }) => ({
    enabled,
    metricId,
    role,
    sortOrder,
  }));

const areMetricRowsEqual = (prevRows, nextRows) =>
  prevRows.length === nextRows.length &&
  prevRows.every((prevRow, index) => {
    const nextRow = nextRows[index];
    return (
      prevRow.enabled === nextRow.enabled &&
      prevRow.metricId === nextRow.metricId &&
      prevRow.role === nextRow.role &&
      prevRow.sortOrder === nextRow.sortOrder
    );
  });

const FactViewSectionMetricConfig = ({
  existingMetrics = EMPTY_ARRAY,
  metrics = EMPTY_ARRAY,
  metricRules = EMPTY_ARRAY,
  onMetricsChange,
  renderExpression,
}) => {
  const form = Form.useFormInstance();
  const includeSummary = Form.useWatch('includeSummary', form);
  const summaryMetricId = Form.useWatch('summaryMetricId', form);
  const selectedItemMetricIds = Form.useWatch('itemMetricIds', form);
  const [items, setItems] = useState([]);

  const metricsById = useMemo(
    () => new Map(metrics.map((metric) => [metric.id, metric])),
    [metrics],
  );
  const ruleByMetricId = useMemo(
    () => new Map(metricRules.map((rule) => [rule.metricId, rule])),
    [metricRules],
  );
  const existingState = useMemo(() => getExistingItemState(existingMetrics), [existingMetrics]);
  const summaryMetric = metricsById.get(summaryMetricId);
  const summaryRule = ruleByMetricId.get(summaryMetricId);
  const metricOptions = buildMetricOptions(metrics);
  const compositeMetricOptions = buildMetricOptions(
    metrics.filter((metric) => metric.metricType === 'composite'),
  );
  const tableRows = useMemo(
    () =>
      includeSummary && summaryMetric
        ? [
            {
              enabled: true,
              metricId: summaryMetric.id,
              metricName: summaryMetric.name,
              role: 'summary',
              sortOrder: 0,
            },
            ...items,
          ]
        : items,
    [includeSummary, items, summaryMetric],
  );
  const saveMetricRows = useMemo(() => toSaveMetricRows(tableRows), [tableRows]);

  useEffect(() => {
    const itemMetricIds = includeSummary
      ? extractMetricIdsFromExpression(summaryRule?.expression, metrics).filter(
          (metricId) => metricId !== summaryMetricId,
        )
      : selectedItemMetricIds || [];
    const nextItems = buildItemRows(itemMetricIds, metricsById, existingState);
    setItems((currentItems) =>
      areMetricRowsEqual(currentItems, nextItems) ? currentItems : nextItems,
    );
  }, [
    existingState,
    includeSummary,
    metrics,
    metricsById,
    selectedItemMetricIds,
    summaryMetricId,
    summaryRule,
  ]);

  useEffect(() => {
    form.setFieldValue('metrics', saveMetricRows);
    onMetricsChange?.(saveMetricRows);
  }, [form, onMetricsChange, saveMetricRows]);

  const updateItem = (metricId, patch) => {
    setItems((currentItems) =>
      currentItems
        .map((item) => (item.metricId === metricId ? { ...item, ...patch } : item))
        .sort((prev, next) => prev.sortOrder - next.sortOrder),
    );
  };

  const columns = [
    {
      title: '指标名称',
      dataIndex: 'metricName',
      render: (value) => <Tag color="geekblue">{value}</Tag>,
    },
    {
      title: '指标角色',
      dataIndex: 'role',
      width: 120,
      render: (role) => (
        <Tag color={role === 'summary' ? 'processing' : 'default'}>
          {role === 'summary' ? '汇总标题' : '普通指标'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 120,
      render: (_, record) => (
        <InputNumber
          disabled={record.role === 'summary'}
          min={0}
          precision={0}
          value={record.sortOrder}
          onChange={(value) => updateItem(record.metricId, { sortOrder: Number(value || 0) })}
        />
      ),
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      width: 100,
      render: (_, record) => (
        <Switch
          disabled={record.role === 'summary'}
          checked={record.enabled}
          onChange={(checked) => updateItem(record.metricId, { enabled: checked })}
        />
      ),
    },
  ];

  return (
    <Flex vertical gap="middle">
      <ProFormSwitch name="includeSummary" label="包含汇总标题指标" />
      {includeSummary ? (
        <Flex vertical gap="middle">
          <ProFormSelect
            name="summaryMetricId"
            label="汇总标题指标"
            options={compositeMetricOptions}
            rules={[{ required: true, message: '请选择汇总标题指标' }]}
            fieldProps={{
              optionFilterProp: 'label',
              showSearch: true,
            }}
          />
          <Space wrap size={[4, 8]}>
            <Typography.Text type="secondary">计算表达式：</Typography.Text>
            {summaryRule?.expression ? (
              renderExpression(summaryRule.expression)
            ) : (
              <Typography.Text type="secondary">暂无计算表达式</Typography.Text>
            )}
          </Space>
          {summaryMetric && (
            <Space wrap size={[4, 8]}>
              <Typography.Text type="secondary">汇总标题：</Typography.Text>
              <Tag color="processing">{summaryMetric.name}</Tag>
            </Space>
          )}
        </Flex>
      ) : (
        <ProFormSelect
          name="itemMetricIds"
          label="普通指标"
          options={metricOptions}
          fieldProps={{
            mode: 'multiple',
            optionFilterProp: 'label',
            showSearch: true,
          }}
        />
      )}
      <Table
        rowKey="metricId"
        columns={columns}
        dataSource={tableRows}
        pagination={false}
        locale={{ emptyText: '暂无普通指标' }}
      />
    </Flex>
  );
};

FactViewSectionMetricConfig.propTypes = {
  existingMetrics: PropTypes.arrayOf(
    PropTypes.shape({
      enabled: PropTypes.bool,
      metricId: PropTypes.string,
      role: PropTypes.string,
      sortOrder: PropTypes.number,
    }),
  ),
  metricRules: PropTypes.arrayOf(
    PropTypes.shape({
      expression: PropTypes.string,
      metricId: PropTypes.string,
    }),
  ),
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      metricType: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  onMetricsChange: PropTypes.func,
  renderExpression: PropTypes.func.isRequired,
};

export default FactViewSectionMetricConfig;
